import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { type Band, dotPos } from "@/lib/setup/lanes";
import { cn } from "@/lib/utils";

/** idle: an unticked suggestion past the first few, not loaded until asked.
 *  checking: the lane preview is still choosing which suggestions load. */
export type LaneState = "idle" | "checking" | "loading" | "ready" | "failed" | "capped";

export const BAND_BG: Record<Band, string> = {
  3: "bg-green-400", 2: "bg-yellow-400", 1: "bg-muted-foreground/50", 0: "bg-muted-foreground/50",
};

/** One seed row's strip (board C): the papers the seed brings in, placed and
 *  coloured by quick-check band, plus "about N papers · ~k on question".
 *  k scales the checked share to N, so it can exceed the green dots: "~" and
 *  the tooltip say so (Brad, 2026-11-02). */
export function SeedLane({ state, dots, reach, onEstimate, share, note, onRetry, onShow }: {
  state: LaneState;
  dots: { key: string; band: Band }[];
  reach: number | null;
  onEstimate: number;
  share: number;
  note?: string | null;
  onRetry?: () => void;
  onShow?: () => void;
}) {
  const on = dots.filter((d) => d.band === 3).length;
  const partly = dots.filter((d) => d.band === 2).length;
  return (
    <>
      <div className="flex min-w-[180px] flex-[1_1_220px] items-center self-center">
        {state === "idle" ? (
          <Button variant="link" className="h-auto p-0 text-sm" onClick={onShow}>Show papers</Button>
        ) : state === "checking" ? (
          <span role="status" className="animate-pulse text-sm text-muted-foreground">Checking…</span>
        ) : state === "loading" ? (
          <div data-testid="lane-loading" className="h-[34px] w-full animate-pulse rounded-md border bg-muted/40" />
        ) : state === "failed" && !dots.length ? (
          <span className="text-sm text-muted-foreground">
            Couldn't check ·{" "}
            <Button variant="link" className="h-auto p-0 text-sm" onClick={onRetry}>Retry</Button>
          </span>
        ) : state === "capped" && !dots.length ? (
          <span className="text-sm text-muted-foreground">Quick check unavailable today</span>
        ) : (
          <div role="img" aria-label={`${dots.length} papers checked: ${on} on your question, ${partly} partly`}
            className="relative h-[34px] w-full rounded-md border bg-muted/40">
            <span aria-hidden className="absolute inset-y-0 left-3/4 border-l border-dashed border-green-400/60" />
            {dots.map((d) => {
              const pos = dotPos(d.key, d.band);
              return (
                <span key={d.key} data-testid="lane-dot" aria-hidden
                  className={cn("absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-85", BAND_BG[d.band])}
                  style={{ left: `${pos.left}%`, top: `${pos.top}%` }} />
              );
            })}
          </div>
        )}
      </div>
      <div className="flex w-28 flex-col items-end justify-center gap-0.5 text-right tabular-nums">
        {note ? (
          <span className="text-sm text-muted-foreground">{note}</span>
        ) : reach != null && state !== "loading" && state !== "idle" && state !== "checking" ? (
          <>
            <span className="text-sm">about {reach} papers</span>
            {state === "ready" && dots.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className={cn("cursor-help text-xs underline decoration-dotted underline-offset-2",
                      share >= 0.4 ? "text-green-400" : "text-muted-foreground")}>
                      {dots.length < reach ? "~" : ""}{onEstimate} on question
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {dots.length < reach
                      ? `${on} of ${dots.length} checked papers are on your question; scaled to about ${reach}.`
                      : `${on} of ${dots.length} checked papers are on your question.`}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}
